import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Button, Stack, Typography } from '@mui/material'

import PageContainer from '@/components/ui/containers/PageContainer'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

import { useGetContractByEstablishmentQuery } from '@/core/services/contract'
import { useDeleteLicenseMutation, useDeleteLicensesMutation, useGetLicensesQuery } from '@/core/services/license'
import { Role } from '@/core/types/shared'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'
import { isAdmin, isStaff, isSuperAdmin } from '@/utils/type-guards'

import LicensesTable, { columnHelper } from './components/LicensesTable'

export default function LicensesPage() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)
  const isAdminUser = isAdmin(authUser)
  const isSuperAdminUser = authUser?.roles.includes(Role.SuperAdmin) ?? false
  const isEstablishmentAdmin = isAdminUser && !isSuperAdminUser

  const { data: contract } = useGetContractByEstablishmentQuery(authUser?.establishment?._id ?? '', {
    skip: !isEstablishmentAdmin || !authUser?.establishment,
    refetchOnMountOrArgChange: true,
  })
  const { data: licenses } = useGetLicensesQuery(
    {
      ...(isEstablishmentAdmin && { establishment: authUser?.establishment?._id }),
      ...(isStaff(authUser) && { id: authUser.licenses }),
    },
    {
      skip: !isEstablishmentAdmin && !isSuperAdminUser && (!isStaff(authUser) || !authUser.licenses.length),
      refetchOnMountOrArgChange: true,
    }
  )
  const [deleteLicense, { isLoading: isDeletingLicense }] = useDeleteLicenseMutation()
  const [deleteLicenses, { isLoading: isDeletingLicenses }] = useDeleteLicensesMutation()

  const [deletingLicenseId, setDeletingLicenseId] = useState<string>()
  const [deletingLicenseIds, setDeletingLicenseIds] = useState<string[]>()
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const extraColumns = useMemo(
    () => [
      columnHelper.accessor((row) => row.quantity, {
        id: 'quantity',
        header: t('table.licenses.header.quantity').toString(),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor((row) => row.licensees, {
        id: 'licensees',
        header: t('table.licenses.header.licensees').toString(),
        cell: ({ getValue }) => getValue(),
      }),
    ],
    [t]
  )

  const licensesData = useMemo(
    () =>
      licenses?.map((license) => ({
        id: license._id,
        canDelete: isSuperAdmin(authUser),
        description: license.description,
        licensees: contract?.licenseUsages.find(({ license: licenseId }) => licenseId === license._id)?.licensees.length,
        name: license.name,
        quantity: contract?.licenseUsages.find(({ license: licenseId }) => licenseId === license._id)?.quantity,
      })) ?? [],
    [authUser, contract, licenses]
  )

  return (
    <PageContainer>
      <Stack spacing={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h2" data-testid="licenses-page-title">
            {t('page.licenses.title')}
          </Typography>
          <Stack direction="row">
            {isSuperAdmin(authUser) && (
              <Button component={Link} to={`${pathname}/create`} variant="contained" data-testid="license-create-button">
                {t('common:button.create.label')}
              </Button>
            )}
          </Stack>
        </Stack>
        <LicensesTable
          data={licensesData}
          extraColumns={isEstablishmentAdmin ? extraColumns : undefined}
          onDeleteClick={(licenseId) => {
            setDeletingLicenseId(licenseId)
            setOpenDeleteConfirmationDialog(true)
          }}
          onDeleteMany={
            isSuperAdmin(authUser)
              ? (deletingLicenses) => {
                setDeletingLicenseIds(deletingLicenses.map((license) => license.id))
                setOpenDeleteConfirmationDialog(true)
              }
              : undefined
          }
          onOpenClick={(licenseId) => {
            const pathPrefix = isSuperAdmin(authUser) ? '/super-admin' : isAdmin(authUser) ? '/admin' : ''
            navigate(`${pathPrefix}/licenses/${licenseId}`)
          }}
          selectable={isSuperAdmin(authUser)}
        />
      </Stack>
      <DeleteConfirmationDialog
        isDeleting={isDeletingLicense || isDeletingLicenses}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          try {
            if (deletingLicenseId) {
              await deleteLicense(deletingLicenseId).unwrap()
              setDeletingLicenseId(undefined)
            }
            if (deletingLicenseIds) {
              await deleteLicenses(deletingLicenseIds).unwrap()
              setDeletingLicenseIds(undefined)
            }
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.license.delete.error')), { variant: 'error' })
          }
          setOpenDeleteConfirmationDialog(false)
        }}
        open={openDeleteConfirmationDialog}
        title={t(`dialog.deleteConfirmation.${deletingLicenseId ? 'license' : deletingLicenseIds ? 'licenses' : ''}.title`)}
        message={t(`dialog.deleteConfirmation.${deletingLicenseId ? 'license' : deletingLicenseIds ? 'licenses' : ''}.message`)}
      />
    </PageContainer>
  )
}
